import { useState, useEffect } from 'react'
import { useQuietMode } from './QuietModeProvider'
import type { RoutineBlock, RoutineTask, RoutineStep } from '../utils/routineStorage'

interface CustomRoutineCreatorProps {
  editingRoutine?: RoutineBlock | null
  onSave: (routine: RoutineBlock) => void
  onCancel: () => void
}

const CustomRoutineCreator = ({ editingRoutine, onSave, onCancel }: CustomRoutineCreatorProps) => {
  const { isQuietMode } = useQuietMode()
  const [currentStep, setCurrentStep] = useState<'basic' | 'tasks' | 'review'>('basic')
  const [routineName, setRoutineName] = useState('')
  const [routineDescription, setRoutineDescription] = useState('')
  const [timeRange, setTimeRange] = useState<'morning' | 'afternoon' | 'evening' | 'anytime'>('anytime')
  const [tasks, setTasks] = useState<RoutineTask[]>([])
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0)

  // Initialize with editing data if provided
  useEffect(() => {
    if (editingRoutine) {
      setRoutineName(editingRoutine.name)
      setRoutineDescription(editingRoutine.description || '')
      setTimeRange(editingRoutine.time_range)
      setTasks(editingRoutine.tasks)
    }
  }, [editingRoutine])

  const createEmptyTask = (): RoutineTask => ({
    id: crypto.randomUUID(),
    block_id: '',
    task_name: '',
    description: '',
    estimated_minutes: 5,
    is_essential: false,
    order_index: tasks.length,
    steps: [createEmptyStep()],
    created_at: new Date(),
    updated_at: new Date()
  })

  const createEmptyStep = (): RoutineStep => ({
    id: crypto.randomUUID(),
    task_id: '',
    step_text: '',
    order_index: 0,
    is_optional: false,
    created_at: new Date()
  })

  const handleBasicInfoNext = () => {
    if (routineName.trim()) {
      if (tasks.length === 0) {
        setTasks([createEmptyTask()])
      }
      setCurrentStep('tasks')
    }
  }

  const addTask = () => {
    setTasks([...tasks, createEmptyTask()])
    setCurrentTaskIndex(tasks.length)
  }

  const updateTask = (index: number, updates: Partial<RoutineTask>) => {
    const updatedTasks = tasks.map((task, i) => 
      i === index ? { ...task, ...updates, updated_at: new Date() } : task
    )
    setTasks(updatedTasks)
  }

  const deleteTask = (index: number) => {
    const updatedTasks = tasks.filter((_, i) => i !== index)
    setTasks(updatedTasks)
    if (currentTaskIndex >= updatedTasks.length) {
      setCurrentTaskIndex(Math.max(0, updatedTasks.length - 1))
    }
  }

  const addStep = (taskIndex: number) => {
    const task = tasks[taskIndex]
    const newStep = {
      ...createEmptyStep(),
      task_id: task.id,
      order_index: task.steps.length
    }
    
    updateTask(taskIndex, {
      steps: [...task.steps, newStep]
    })
  }

  const updateStep = (taskIndex: number, stepIndex: number, updates: Partial<RoutineStep>) => {
    const task = tasks[taskIndex]
    const updatedSteps = task.steps.map((step, i) => 
      i === stepIndex ? { ...step, ...updates } : step
    )
    updateTask(taskIndex, { steps: updatedSteps })
  }

  const deleteStep = (taskIndex: number, stepIndex: number) => {
    const task = tasks[taskIndex]
    if (task.steps.length > 1) {
      const updatedSteps = task.steps.filter((_, i) => i !== stepIndex)
      updateTask(taskIndex, { steps: updatedSteps })
    }
  }

  const handleSave = () => {
    const routine: RoutineBlock = {
      id: editingRoutine?.id || crypto.randomUUID(),
      user_id: editingRoutine?.user_id,
      name: routineName.trim(),
      description: routineDescription.trim() || undefined,
      time_range: timeRange,
      is_active: true,
      order_index: editingRoutine?.order_index || 0,
      tasks: tasks.map((task, index) => ({
        ...task,
        block_id: editingRoutine?.id || crypto.randomUUID(),
        order_index: index,
        steps: task.steps.map((step, stepIndex) => ({
          ...step,
          task_id: task.id,
          order_index: stepIndex
        }))
      })),
      created_at: editingRoutine?.created_at || new Date(),
      updated_at: new Date()
    }

    onSave(routine)
  }

  const canProceedToReview = () => {
    return tasks.length > 0 && 
           tasks.every(task => 
             task.task_name.trim() && 
             task.steps.length > 0 && 
             task.steps.every(step => step.step_text.trim())
           )
  }

  const getTotalEstimatedTime = () => {
    return tasks.reduce((total, task) => total + task.estimated_minutes, 0)
  }

  const getOptionalStepsCount = (task: RoutineTask) => {
    return task.steps.filter(step => step.is_optional).length
  }

  const getRequiredStepsCount = (task: RoutineTask) => {
    return task.steps.filter(step => !step.is_optional).length
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-cream-25 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-cream-25 border-b border-cream-100/50 p-6 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <h2 className="text-cream-600 font-light text-xl">
              {editingRoutine ? 'Edit Routine' : 'Create Custom Routine'}
            </h2>
            <button
              onClick={onCancel}
              className="p-2 rounded-xl hover:bg-cream-100/60 transition-colors focus:outline-none focus:ring-2 focus:ring-cream-100"
            >
              <svg className="w-5 h-5 text-cream-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center space-x-4 mt-6">
            {['basic', 'tasks', 'review'].map((step, index) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-light ${
                  currentStep === step 
                    ? 'bg-cream-200 text-cream-700' 
                    : index < ['basic', 'tasks', 'review'].indexOf(currentStep)
                    ? 'bg-mint-200 text-mint-700'
                    : 'bg-cream-100 text-cream-500'
                }`}>
                  {index + 1}
                </div>
                {index < 2 && (
                  <div className={`w-8 h-px mx-2 ${
                    index < ['basic', 'tasks', 'review'].indexOf(currentStep)
                      ? 'bg-mint-200'
                      : 'bg-cream-100'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Basic Info */}
          {currentStep === 'basic' && (
            <div className="space-y-6">
              <div>
                <label className="block text-cream-600 font-light text-sm mb-2">
                  Routine Name *
                </label>
                <input
                  type="text"
                  value={routineName}
                  onChange={(e) => setRoutineName(e.target.value)}
                  placeholder="e.g., My Morning Flow, Evening Calm..."
                  className="w-full p-4 rounded-2xl border border-cream-200/50 focus:border-cream-300 focus:ring-2 focus:ring-cream-100 focus:outline-none font-light text-sm bg-white/50"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-cream-600 font-light text-sm mb-2">
                  Description (optional)
                </label>
                <textarea
                  value={routineDescription}
                  onChange={(e) => setRoutineDescription(e.target.value)}
                  placeholder="What is this routine for? How does it help you?"
                  className="w-full p-4 rounded-2xl border border-cream-200/50 focus:border-cream-300 focus:ring-2 focus:ring-cream-100 focus:outline-none font-light text-sm bg-white/50 resize-none"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-cream-600 font-light text-sm mb-2">
                  When do you want to do this?
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'morning', label: 'Morning', emoji: '🌅' },
                    { value: 'afternoon', label: 'Afternoon', emoji: '☀️' },
                    { value: 'evening', label: 'Evening', emoji: '🌙' },
                    { value: 'anytime', label: 'Anytime', emoji: '⏰' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setTimeRange(option.value as any)}
                      className={`p-4 rounded-2xl border transition-colors focus:outline-none focus:ring-2 focus:ring-cream-100 ${
                        timeRange === option.value
                          ? 'bg-cream-100 border-cream-200 text-cream-700'
                          : 'bg-white/50 border-cream-100/50 text-cream-600 hover:bg-cream-50'
                      }`}
                    >
                      <div className="text-lg mb-1">{option.emoji}</div>
                      <div className="font-light text-sm">{option.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleBasicInfoNext}
                  disabled={!routineName.trim()}
                  className="px-8 py-3 bg-cream-100/80 hover:bg-cream-200/60 
                             text-cream-600 rounded-2xl text-base font-light border border-cream-200/50 hover:border-cream-300/60
                             transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-cream-100
                             disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next: Add Tasks
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Tasks */}
          {currentStep === 'tasks' && (
            <div className="space-y-6">
              {/* Task Tabs */}
              <div className="flex space-x-2 overflow-x-auto pb-2">
                {tasks.map((task, index) => (
                  <button
                    key={task.id}
                    onClick={() => setCurrentTaskIndex(index)}
                    className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-light transition-colors ${
                      currentTaskIndex === index
                        ? 'bg-cream-100 text-cream-700 border border-cream-200'
                        : 'bg-white/50 text-cream-600 border border-cream-100/50 hover:bg-cream-50'
                    }`}
                  >
                    {task.task_name || `Task ${index + 1}`}
                  </button>
                ))}
                <button
                  onClick={addTask}
                  className="flex-shrink-0 px-4 py-2 rounded-xl text-sm font-light bg-mint-50/60 text-mint-600 border border-mint-100/50 hover:bg-mint-100/50 transition-colors"
                >
                  + Add Task
                </button>
              </div>

              {/* Current Task Editor */}
              {tasks[currentTaskIndex] && (
                <div className="bg-white/60 rounded-2xl border border-cream-100/50 p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-cream-600 font-light text-lg">
                      Task {currentTaskIndex + 1}
                    </h3>
                    {tasks.length > 1 && (
                      <button
                        onClick={() => deleteTask(currentTaskIndex)}
                        className="p-2 rounded-xl hover:bg-peach-100/60 transition-colors focus:outline-none focus:ring-2 focus:ring-peach-100"
                      >
                        <svg className="w-4 h-4 text-peach-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="block text-cream-600 font-light text-sm mb-2">
                      Task Name *
                    </label>
                    <input
                      type="text"
                      value={tasks[currentTaskIndex].task_name}
                      onChange={(e) => updateTask(currentTaskIndex, { task_name: e.target.value })}
                      placeholder="e.g., Deep breathing, Gentle stretching..."
                      className="w-full p-3 rounded-xl border border-cream-200/50 focus:border-cream-300 focus:ring-2 focus:ring-cream-100 focus:outline-none font-light text-sm bg-white/50"
                    />
                  </div>

                  <div>
                    <label className="block text-cream-600 font-light text-sm mb-2">
                      Description (optional)
                    </label>
                    <input
                      type="text"
                      value={tasks[currentTaskIndex].description || ''}
                      onChange={(e) => updateTask(currentTaskIndex, { description: e.target.value })}
                      placeholder="What does this task help with?"
                      className="w-full p-3 rounded-xl border border-cream-200/50 focus:border-cream-300 focus:ring-2 focus:ring-cream-100 focus:outline-none font-light text-sm bg-white/50"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-cream-600 font-light text-sm mb-2">
                        Estimated Minutes
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="60"
                        value={tasks[currentTaskIndex].estimated_minutes}
                        onChange={(e) => updateTask(currentTaskIndex, { estimated_minutes: parseInt(e.target.value) || 5 })}
                        className="w-full p-3 rounded-xl border border-cream-200/50 focus:border-cream-300 focus:ring-2 focus:ring-cream-100 focus:outline-none font-light text-sm bg-white/50"
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tasks[currentTaskIndex].is_essential}
                          onChange={(e) => updateTask(currentTaskIndex, { is_essential: e.target.checked })}
                          className="rounded border-cream-200 text-mint-500 focus:ring-mint-100"
                        />
                        <span className="text-cream-600 font-light text-sm">Essential task</span>
                      </label>
                    </div>
                  </div>

                  {/* Steps */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <label className="text-cream-600 font-light text-sm">
                          Steps *
                        </label>
                        {tasks[currentTaskIndex].steps.length > 0 && (
                          <p className="text-cream-500 font-light text-xs mt-1">
                            {getRequiredStepsCount(tasks[currentTaskIndex])} required, {getOptionalStepsCount(tasks[currentTaskIndex])} optional
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => addStep(currentTaskIndex)}
                        className="px-3 py-1 bg-mint-50/60 text-mint-600 rounded-lg text-xs font-light border border-mint-100/50 hover:bg-mint-100/50 transition-colors"
                      >
                        + Add Step
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      {tasks[currentTaskIndex].steps.map((step, stepIndex) => (
                        <div key={step.id} className="bg-cream-50/40 rounded-lg p-3 space-y-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-cream-400 text-sm font-light w-6">
                              {stepIndex + 1}.
                            </span>
                            <input
                              type="text"
                              value={step.step_text}
                              onChange={(e) => updateStep(currentTaskIndex, stepIndex, { step_text: e.target.value })}
                              placeholder="Describe this step..."
                              className="flex-1 p-2 rounded-lg border border-cream-200/50 focus:border-cream-300 focus:ring-1 focus:ring-cream-100 focus:outline-none font-light text-sm bg-white/50"
                            />
                            {tasks[currentTaskIndex].steps.length > 1 && (
                              <button
                                onClick={() => deleteStep(currentTaskIndex, stepIndex)}
                                className="p-1 rounded-lg hover:bg-peach-100/60 transition-colors focus:outline-none focus:ring-1 focus:ring-peach-100"
                              >
                                <svg className="w-3 h-3 text-peach-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                          
                          {/* Optional step toggle */}
                          <div className="flex items-center space-x-2 ml-8">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={step.is_optional}
                                onChange={(e) => updateStep(currentTaskIndex, stepIndex, { is_optional: e.target.checked })}
                                className="rounded border-cream-200 text-sage-500 focus:ring-sage-100 w-3 h-3"
                              />
                              <span className="text-cream-500 font-light text-xs">
                                Optional step
                              </span>
                            </label>
                            {step.is_optional && (
                              <span className="px-2 py-1 bg-sage-100/60 text-sage-600 text-xs font-light rounded-full">
                                Can skip
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Helper text about optional steps */}
                    {!isQuietMode && (
                      <div className="mt-3 p-3 bg-sage-50/40 rounded-lg border border-sage-100/30">
                        <p className="text-sage-600 font-light text-xs leading-relaxed">
                          💡 <strong>Optional steps</strong> can be skipped when you're low on energy or time. 
                          They're gentle extras that enhance the routine but aren\'t required.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep('basic')}
                  className="px-6 py-3 bg-cream-50/60 hover:bg-cream-100/50 
                             text-cream-600 rounded-2xl text-sm font-light border border-cream-100/40 hover:border-cream-200/50
                             transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-cream-100/50"
                >
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep('review')}
                  disabled={!canProceedToReview()}
                  className="px-8 py-3 bg-cream-100/80 hover:bg-cream-200/60 
                             text-cream-600 rounded-2xl text-base font-light border border-cream-200/50 hover:border-cream-300/60
                             transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-cream-100
                             disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Review & Save
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {currentStep === 'review' && (
            <div className="space-y-6">
              <div className="bg-white/60 rounded-2xl border border-cream-100/50 p-6">
                <h3 className="text-cream-600 font-light text-lg mb-4">Routine Overview</h3>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-cream-500 font-light text-sm">Name:</span>
                    <span className="text-cream-600 font-light text-sm">{routineName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-cream-500 font-light text-sm">Time:</span>
                    <span className="text-cream-600 font-light text-sm capitalize">{timeRange}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-cream-500 font-light text-sm">Tasks:</span>
                    <span className="text-cream-600 font-light text-sm">{tasks.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-cream-500 font-light text-sm">Estimated Time:</span>
                    <span className="text-cream-600 font-light text-sm">~{getTotalEstimatedTime()} minutes</span>
                  </div>
                </div>

                {routineDescription && (
                  <div className="mb-6">
                    <p className="text-cream-500 font-light text-sm mb-2">Description:</p>
                    <p className="text-cream-600 font-light text-sm leading-relaxed">
                      {routineDescription}
                    </p>
                  </div>
                )}

                <div>
                  <h4 className="text-cream-600 font-light text-base mb-3">Tasks:</h4>
                  <div className="space-y-3">
                    {tasks.map((task, index) => (
                      <div key={task.id} className="bg-cream-50/60 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="text-cream-600 font-light text-sm">{task.task_name}</h5>
                          <div className="flex items-center space-x-2">
                            <span className="text-cream-500 text-xs font-light">
                              {task.estimated_minutes}min
                            </span>
                            {task.is_essential && (
                              <span className="px-2 py-1 bg-mint-100/60 text-mint-600 text-xs font-light rounded-full">
                                Essential
                              </span>
                            )}
                          </div>
                        </div>
                        {task.description && (
                          <p className="text-cream-500 font-light text-xs mb-2">
                            {task.description}
                          </p>
                        )}
                        <div className="space-y-1">
                          {task.steps.map((step, stepIndex) => (
                            <div key={step.id} className="flex items-center space-x-2">
                              <div className={`w-1 h-1 rounded-full ${step.is_optional ? 'bg-sage-300' : 'bg-cream-300'}`}></div>
                              <span className={`text-xs font-light ${step.is_optional ? 'text-sage-500 italic' : 'text-cream-500'}`}>
                                {step.step_text}
                                {step.is_optional && <span className="ml-1">(optional)</span>}
                              </span>
                            </div>
                          ))}
                        </div>
                        
                        {/* Step summary */}
                        {getOptionalStepsCount(task) > 0 && (
                          <div className="mt-2 pt-2 border-t border-cream-200/30">
                            <p className="text-cream-400 text-xs font-light">
                              {getRequiredStepsCount(task)} required steps, {getOptionalStepsCount(task)} optional
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep('tasks')}
                  className="px-6 py-3 bg-cream-50/60 hover:bg-cream-100/50 
                             text-cream-600 rounded-2xl text-sm font-light border border-cream-100/40 hover:border-cream-200/50
                             transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-cream-100/50"
                >
                  Back to Edit
                </button>
                <button
                  onClick={handleSave}
                  className="px-8 py-3 bg-mint-100/80 hover:bg-mint-200/60 
                             text-mint-600 rounded-2xl text-base font-light border border-mint-200/50 hover:border-mint-300/60
                             transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-mint-100"
                >
                  {editingRoutine ? 'Save Changes' : 'Create Routine'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CustomRoutineCreator